/* eslint-disable max-lines */
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchCompany } from 'api/endpoints/company';
import { fetchUserMe, updateCurrentUser } from 'api/endpoints/users';
import { fetchVisibilityGroupById } from 'api/endpoints/visibility-groups';
import { useNotificationsPoller } from 'hooks/use-notifications-poller';

import {
  checkAdminAccess,
  checkCuratorAccess,
  checkReportsAccess,
  checkTagVisibility,
  checkCardInfoVisibility,
  checkImpersonating,
  checkCompanyLinkDefault,
  checkCardSentimentAccess,
  checkDashboardNewsAccess,
  checkCardFeedbackDisabled,
  checkCardSentimentAndFeedbackOnHoverDisabled,
  checkIsChatExportEnabled,
  checkIsWinLossEnabled,
  checkWinLossVisibilityGroupPermitted,
  checkIsWinLossExclusive,
  checkIsHatchExclusive,
  checkIsCopyToBoardsEnabled,
  checkCompetitiveAnalyticsOverviewReportEnabled,
  checkCompetitiveAnalyticsThreatAnalysisReportEnabled,
  checkCompetitiveAnalyticsRepPerformanceReportEnabled,
  checkCompetitiveAnalyticsDataHygieneReportEnabled,
  checkCompetitiveAnalyticsImpactReportEnabled,
  checkIsConsumerReportEnabledForCurators,
  checkIsCuratorReportEnabledForCurators,
  checkIsCRAReportEnabledForCurators,
  checkisCompetitiveDashboardEnabledForConsumers,
  checkAskKlueEnabled,
  checkIsCompetitiveAnalyticsProgramImpactReportEnabled,
  checkIsFeedHiddenInConsumerNavEnabled,
  checkIsInsightsWhyWeWinLoseEnabled,
  checkIsCallInsightsDemoReplacementEnabled,
  checkDashboardNewsToggleDefault,
  checkIfNewCardEditorEnabled,
  checkIfContentEditorV3Enabled,
  checkIfCreateAIBattlecardEnabled,
  checkIsEditorTemplateBlocksEnabled,
  checkIsSFDCDataSyncUserRoleEnabled,
  checkIsGenAISearchResultsEnabled,
  checkCompanyLandingPageEnabled,
  checkCompanyFavoritesEnabled,
  checkIsAskKlueWizardEnabled,
  checkIsConsumerAskKlueToggleHidden,
  checkIsDashboardAskKluePreviewForCuratorsEnabled,
  checkIsDashboardAskKlueEnabled,
  checkKnowledgeHubEnabled,
  checkIsBattlecardAskKlueEnabled,
  checkUpdateSummaryEnabled,
  checkIsAskKlueExportEnabled,
  checkIsAskKlueExportEnabledForCurators,
  checkIsSFDSProductMappingEnabled,
  checkIsSFDSWriteBackEnabled,
  checkIsHSDSProductMappingEnabled,
  checkIsHSDSWriteBackEnabled,
  checkIsUnifiedSearchEnabled,
  checkIsAskKlueDashboardPageEnabled,
  checkIsPromptLibraryEnabled,
  checkIsCallInsightsDisabled,
  checkIsDealTipsEnabled,
  checkIsZoomStatusSettingsVisible,
} from './auth.utils';

import type {
  OptionalUserType,
  OptionalCompanyType,
  CompanyLinkDefaultType,
} from './auth.types';
import type { StatusError } from 'api/interceptors/statusErrorInterceptor';
import type { Dispatch, RootState } from 'store/store.types';

type VisibilityGroupType = number | null;

export default function useAuth() {
  const [loading, setLoading] = useState(true);
  const {
    auth: {
      setCompany,
      setUser,
      setVisibilityGroup,
      signOut: dispatchSignOut,
      revertImpersonating: dispatchRevertImpersonating,
    },
    visibilityGroups: { populate: populateVisibilityGroups },
  } = useDispatch<Dispatch>();

  const company = useSelector<RootState, OptionalCompanyType>(
    (state) => state.auth.company,
  );
  const user = useSelector<RootState, OptionalUserType>(
    (state) => state.auth.user,
  );
  const visibilityGroup = useSelector<RootState, VisibilityGroupType>(
    (state) => state.auth.visibilityGroup,
  );

  const updateUser = async (
    updateData: Partial<NonNullable<OptionalUserType>>,
  ) => {
    const { data } = await updateCurrentUser({
      params: updateData,
    });

    setUser(data);

    return data;
  };

  const signOut = async () => {
    await dispatchSignOut({});
    setUser(null);
    setLoading(false);
  };

  const revertImpersonatingUser = async () => {
    await dispatchRevertImpersonating({});
  };

  const { startPolling } = useNotificationsPoller();

  const fetchUser = useCallback(async () => {
    try {
      const user = await fetchUserMe();
      const company = await fetchCompany();

      if (isEmpty(user.data) || isEmpty(company.data)) {
        throw Error('auth data incomplete');
      }

      setUser(user.data);
      setCompany(company.data);

      const isCurator = checkCuratorAccess(user.data);

      const previewingParam = new URL(
        document.location.toString(),
      ).searchParams.get('previewing');

      if (isCurator && previewingParam) {
        if (previewingParam !== '0') {
          const { data: visibilityGroup } = await fetchVisibilityGroupById({
            path: { id: +previewingParam },
          });
          if (visibilityGroup.id) {
            populateVisibilityGroups({ visibilityGroups: [visibilityGroup] });
            setVisibilityGroup(visibilityGroup.id);
          }
        } else {
          setVisibilityGroup(0);
        }
      }

      startPolling();
    } catch (error: unknown) {
      setUser(null);
      setCompany(null);

      // Network failures (offline, DNS, timeout) produce "TypeError: Failed to fetch".
      // These are handled gracefully by clearing auth state above — no need to
      // propagate to Airbrake. Other TypeErrors (real bugs) should still throw.
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return;
      }

      const statusError = error as StatusError;
      const apiError =
        (statusError.response?.data as { error?: string })?.error ||
        statusError.message ||
        'Authentication failed';
      throw new Error(apiError, { cause: error });
    } finally {
      setLoading(false);
    }
  }, [
    setUser,
    setCompany,
    startPolling,
    populateVisibilityGroups,
    setVisibilityGroup,
  ]);

  const isAdmin = checkAdminAccess(user, visibilityGroup);
  const isCurator = checkCuratorAccess(user, visibilityGroup);
  const isHatchAdmin = isAdmin || isCurator;
  const isStaff = user?.isStaff || false;

  const isReportsEnabled = checkReportsAccess(company, user, visibilityGroup);
  const isDigestAvailable =
    isAdmin ||
    !company?.reviewRole ||
    !!user?.roles.includes(company?.reviewRole as string);

  const isTagVisible = checkTagVisibility(company, user, visibilityGroup);
  const showCardInfo = checkCardInfoVisibility(company);

  const isDashboardNewsEnabled = checkDashboardNewsAccess(company);
  const isCardFeedbackDisabled = checkCardFeedbackDisabled(company);
  const isCardSentimentEnabled = checkCardSentimentAccess(company);
  const isCardSentimentAndFeedbackOnHoverDisabled =
    checkCardSentimentAndFeedbackOnHoverDisabled(company);
  const isImpersonating = checkImpersonating(user);

  const isChatExportEnabled = checkIsChatExportEnabled(
    company,
    user,
    visibilityGroup,
  );

  const isWinLossEnabled = checkIsWinLossEnabled(
    company,
    user,
    visibilityGroup,
  );

  const isWinLossExclusive = checkIsWinLossExclusive(
    company,
    user,
    visibilityGroup,
  );

  // Separate from isWinLossEnabled on purpose. The nav item is shown to companies that do
  // not have Win-Loss yet, carrying a "NEW" badge, and that promotion should survive. This
  // says only "may this visibility group see Win-Loss at all", so the item disappears
  // entirely for an excluded group while the promo still runs everywhere else.
  const isWinLossVisibleToVisibilityGroup =
    checkWinLossVisibilityGroupPermitted(company, user, visibilityGroup);

  const isHatchExclusive = checkIsHatchExclusive(company, user);

  const isCopyToBoardsEnabled = checkIsCopyToBoardsEnabled(
    company,
    user,
    visibilityGroup,
  );
  const isCompetitiveAnalyticsOverviewReportEnabled =
    checkCompetitiveAnalyticsOverviewReportEnabled(user, company);
  const isCompetitiveAnalyticsThreatAnalysisReportEnabled =
    checkCompetitiveAnalyticsThreatAnalysisReportEnabled(user, company);
  const isCompetitiveAnalyticsRepPerformanceReportEnabled =
    checkCompetitiveAnalyticsRepPerformanceReportEnabled(user, company);
  const isCompetitiveAnalyticsDataHygieneReportEnabled =
    checkCompetitiveAnalyticsDataHygieneReportEnabled(user, company);
  const isCompetitiveAnalyticsImpactReportEnabled =
    checkCompetitiveAnalyticsImpactReportEnabled(user, company);
  const isCompetitiveAnalyticsProgramImpactReportEnabled =
    checkIsCompetitiveAnalyticsProgramImpactReportEnabled(user, company);
  const isConsumerReportEnabledForCurators =
    checkIsConsumerReportEnabledForCurators(company);
  const isCuratorReportEnabledForCurators =
    checkIsCuratorReportEnabledForCurators(company);
  const isCRAReportEnabledForCurators =
    checkIsCRAReportEnabledForCurators(company);
  const isCompetitiveDashboardEnabledForConsumers =
    checkisCompetitiveDashboardEnabledForConsumers(company);
  const isAskKlueExportEnabledForCurators =
    checkIsAskKlueExportEnabledForCurators(company);
  const isAskKlueExportEnabled = checkIsAskKlueExportEnabled(company);

  const isAskKlueEnabled = checkAskKlueEnabled(company, user, visibilityGroup);
  const isDashboardAskKlueEnabled = checkIsDashboardAskKlueEnabled(company);
  const isDashboardAskKluePreviewForCuratorsEnabled =
    checkIsDashboardAskKluePreviewForCuratorsEnabled(company, user);

  const isDashboardNewsToggleDefaultOff =
    checkDashboardNewsToggleDefault(company);

  const isCompanyLandingPageEnabled = checkCompanyLandingPageEnabled(company);
  const isCompanyFavoritesEnabled = checkCompanyFavoritesEnabled(
    company,
    user,
    visibilityGroup,
  );
  const companyLinkDefault: CompanyLinkDefaultType = checkCompanyLinkDefault(
    company,
    user,
    visibilityGroup,
  );
  const isUpdateSummaryEnabled = checkUpdateSummaryEnabled(company);

  const isSFDCDataSyncUserRoleEnabled =
    checkIsSFDCDataSyncUserRoleEnabled(company);

  const isSFDSProductMappingEnabled = checkIsSFDSProductMappingEnabled(company);
  const isSFDSWriteBackEnabled = checkIsSFDSWriteBackEnabled(company);

  const isHSDSProductMappingEnabled = checkIsHSDSProductMappingEnabled(company);
  const isHSDSWriteBackEnabled = checkIsHSDSWriteBackEnabled(company);

  const isZoomStatusSettingsVisible = checkIsZoomStatusSettingsVisible(company);

  const isGenAISearchResultsEnabled = checkIsGenAISearchResultsEnabled(company);

  const determineHomeRoute = (): string => {
    if (isHatchExclusive) {
      return '/next';
    }

    if (isWinLossExclusive) {
      return '/win-loss';
    }

    return company?.companyData?.forceHome || '/dashboard';
  };

  const homeRoute = determineHomeRoute();

  const isFeedHiddenInConsumerNavEnabled =
    checkIsFeedHiddenInConsumerNavEnabled(company);

  const isInsightsWhyWeWinLoseEnabled =
    checkIsInsightsWhyWeWinLoseEnabled(company);

  const isCallInsightsDemoReplacementEnabled =
    checkIsCallInsightsDemoReplacementEnabled(company);

  const isNewCardEditorEnabled = checkIfNewCardEditorEnabled(
    company,
    user,
    visibilityGroup,
  );

  const isContentEditorV3Enabled = checkIfContentEditorV3Enabled(
    company,
    user,
    visibilityGroup,
  );

  const isCreateAIBattlecardEnabled = checkIfCreateAIBattlecardEnabled(
    company,
    user,
    visibilityGroup,
  );

  const isEditorTemplateBlocksEnabled =
    checkIsEditorTemplateBlocksEnabled(company);

  const isAskKlueWizardEnabled = checkIsAskKlueWizardEnabled(company);

  const isConsumerAskKlueToggleHidden =
    checkIsConsumerAskKlueToggleHidden(company);

  const isKnowledgeHubEnabled = checkKnowledgeHubEnabled(
    company,
    user,
    visibilityGroup,
  );

  const isCompetitiveDashboardVisible = isNil(
    user?.userData?.isCompetitiveDealWidgetVisible,
  )
    ? user?.userData?.isSeller && isCompetitiveDashboardEnabledForConsumers
    : user?.userData?.isSeller &&
      user?.userData?.isCompetitiveDealWidgetVisible &&
      isCompetitiveDashboardEnabledForConsumers;

  const isBattlecardAskKlueEnabled = checkIsBattlecardAskKlueEnabled(company);
  const isAskKlueDashboardPageEnabled =
    checkIsAskKlueDashboardPageEnabled(company);

  const isUnifiedSearchEnabled = checkIsUnifiedSearchEnabled(company);

  const isPromptLibraryEnabled = checkIsPromptLibraryEnabled(company);

  const isCallInsightsDisabled = checkIsCallInsightsDisabled(company);

  const isDealTipsEnabled = checkIsDealTipsEnabled(company);

  return {
    user,
    company,
    visibilityGroup,
    updateUser,
    companyLinkDefault,
    loading,
    signOut,
    showCardInfo,
    homeRoute,
    isAdmin,
    isCurator,
    isHatchAdmin,
    isStaff,
    isDigestAvailable,
    isReportsEnabled,
    isTagVisible,
    isDashboardNewsEnabled,
    isCardFeedbackDisabled,
    isCardSentimentEnabled,
    isCardSentimentAndFeedbackOnHoverDisabled,
    fetchUser,
    isImpersonating,
    revertImpersonatingUser,
    isChatExportEnabled,
    isWinLossEnabled,
    isWinLossVisibleToVisibilityGroup,
    isWinLossExclusive,
    isHatchExclusive,
    isCopyToBoardsEnabled,
    isCompetitiveAnalyticsOverviewReportEnabled,
    isCompetitiveAnalyticsThreatAnalysisReportEnabled,
    isCompetitiveAnalyticsRepPerformanceReportEnabled,
    isCompetitiveAnalyticsDataHygieneReportEnabled,
    isCompetitiveAnalyticsImpactReportEnabled,
    isCompetitiveAnalyticsProgramImpactReportEnabled,
    isConsumerReportEnabledForCurators,
    isCuratorReportEnabledForCurators,
    isCRAReportEnabledForCurators,
    isAskKlueExportEnabledForCurators,
    isAskKlueExportEnabled,
    isCompetitiveDashboardEnabledForConsumers,
    isAskKlueEnabled,
    isDashboardAskKlueEnabled,
    isBattlecardAskKlueEnabled,
    isDashboardAskKluePreviewForCuratorsEnabled,
    isDashboardNewsToggleDefaultOff,
    isCompanyLandingPageEnabled,
    isCompanyFavoritesEnabled,
    isUpdateSummaryEnabled,
    isSFDCDataSyncUserRoleEnabled,
    isSFDSProductMappingEnabled,
    isSFDSWriteBackEnabled,
    isHSDSProductMappingEnabled,
    isHSDSWriteBackEnabled,
    isZoomStatusSettingsVisible,
    isFeedHiddenInConsumerNavEnabled,
    isInsightsWhyWeWinLoseEnabled,
    isCallInsightsDemoReplacementEnabled,
    isNewCardEditorEnabled,
    isContentEditorV3Enabled,
    isCreateAIBattlecardEnabled,
    isEditorTemplateBlocksEnabled,
    isGenAISearchResultsEnabled,
    isAskKlueWizardEnabled,
    isConsumerAskKlueToggleHidden,
    isKnowledgeHubEnabled,
    isCompetitiveDashboardVisible,
    isUnifiedSearchEnabled,
    isAskKlueDashboardPageEnabled,
    isPromptLibraryEnabled,
    isCallInsightsDisabled,
    isDealTipsEnabled,
  };
}
